import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { analyticsService } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
  LineChart, Line
} from 'recharts'
import {
  TrendingUp, Users, PieChart as PieIcon, LayoutGrid,
  Building2, GraduationCap, Calendar, ShieldCheck, Activity,
  Download, Clock, ChevronDown, ChevronUp, AlertTriangle
} from 'lucide-react'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import clsx from 'clsx'
import toast from 'react-hot-toast'

const COLORS = ['#10b981', '#f59e0b', '#f43f5e', '#4f46e5', '#8b5cf6']

// --- Кастомный тултип в стиле Sentinel ---
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 shadow-[0_20px_50px_rgba(0,0,0,0.1)] rounded-2xl p-4">
        <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 border-b border-slate-50 pb-2">
          {label ? format(new Date(label), 'd MMMM yyyy', { locale: ru }) : 'Данные'}
        </p>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-[11px] font-bold text-slate-600">{entry.name}</span>
              </div>
              <span className="text-[11px] font-black text-slate-900">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }
  return null
}

export default function AnalyticsPage() {
  const user = useAuthStore((s) => s.user)
  const isHR = user?.role_detail?.name === 'hr'

  const [days, setDays] = useState(30)
  const [tab, setTab] = useState('groups')
  const [selectedDeptId, setSelectedDeptId] = useState(null)
  const [exportingDept, setExportingDept] = useState(false)

  const today = new Date()
  const dateFrom = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd')
  const dateTo = format(today, 'yyyy-MM-dd')

  const { data: deptDetail, isLoading: isDeptDetailLoading } = useQuery({
    queryKey: ['emp-dept-detail', selectedDeptId, dateFrom, dateTo],
    queryFn: () => analyticsService.employeeDeptDetail(selectedDeptId, { date_from: dateFrom, date_to: dateTo }).then(r => r.data),
    enabled: !!selectedDeptId,
  })

  const handleExportDeptExcel = async (deptId, deptName) => {
    setExportingDept(true)
    try {
      const resp = await analyticsService.exportEmpDeptExcel(deptId, { date_from: dateFrom, date_to: dateTo })
      const url = URL.createObjectURL(new Blob([resp.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `employees_${deptName}_${dateFrom}_${dateTo}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Excel скачан')
    } catch {
      toast.error('Ошибка экспорта')
    } finally {
      setExportingDept(false)
    }
  }

  // --- API Данные ---
  const { data: trend, isLoading: isTrendLoading } = useQuery({
    queryKey: ['trend', days],
    queryFn: () => analyticsService.trend({ days }).then((r) => r.data),
  })

  const { data: groupStats } = useQuery({
    queryKey: ['group-analytics'],
    queryFn: () => analyticsService.groups().then((r) => r.data),
  })

  const { data: deptStats } = useQuery({
    queryKey: ['dept-analytics'],
    queryFn: () => analyticsService.departments().then((r) => r.data),
  })

  const pieData = [
    { name: 'Вовремя', value: trend?.data?.reduce((a, d) => a + (d.on_time || 0), 0) || 0 },
    { name: 'Опоздали', value: trend?.data?.reduce((a, d) => a + (d.late || 0), 0) || 0 },
  ]

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in duration-500">

      {/* HEADER */}
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white p-5 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-slate-900 rounded-xl flex items-center justify-center">
              <ShieldCheck className="text-white w-5 h-5" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Deep Analytics</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black text-slate-900 tracking-tighter leading-none">Аналитические отчеты</h1>
          <p className="text-slate-500 text-sm font-medium mt-2">Глубокий анализ дисциплины и посещаемости подразделений</p>
        </div>

        {/* Period Selector */}
        <div className="flex p-1.5 bg-slate-100 rounded-2xl border border-slate-200 w-fit">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={clsx(
                "px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                days === d ? "bg-white text-indigo-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-800"
              )}
            >
              {d} Дней
            </button>
          ))}
        </div>
      </header>

      {/* TREND CHART CARD */}
      <section className="bg-white border border-slate-200 rounded-[2.5rem] p-5 md:p-8 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-10">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 rounded-xl border border-indigo-100 text-indigo-600">
              <TrendingUp size={20} />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 tracking-tight">Динамика посещаемости</h3>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Global Protocol Tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest">
            <div className="flex items-center gap-1.5 text-indigo-600"><div className="w-2 h-2 rounded-full bg-indigo-600" /> Всего</div>
            <div className="flex items-center gap-1.5 text-emerald-500"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Вовремя</div>
          </div>
        </div>

        <div className="h-[220px] md:h-[350px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend?.data || []}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }}
                tickFormatter={(v) => format(new Date(v), 'd MMM', { locale: ru }).toUpperCase()}
                dy={10}
              />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }} dx={-10} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="total" name="Всего" stroke="#4f46e5" strokeWidth={4} dot={false} animationDuration={1000} />
              <Line type="monotone" dataKey="on_time" name="Вовремя" stroke="#10b981" strokeWidth={4} dot={false} animationDuration={1500} />
              <Line type="monotone" dataKey="late" name="Опоздали" stroke="#f59e0b" strokeWidth={4} dot={false} strokeDasharray="8 8" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* DISTRIBUTION (PIE) */}
        <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm flex flex-col items-center">
          <div className="w-full flex items-center gap-3 mb-8">
            <div className="p-2.5 bg-rose-50 rounded-xl border border-rose-100 text-rose-600">
              <PieIcon size={20} />
            </div>
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Распределение</h3>
          </div>

          <div className="relative h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={70} outerRadius={95} paddingAngle={8} dataKey="value" stroke="none">
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-3xl font-black text-slate-900 tracking-tighter">
                {Math.round((pieData[0].value / (pieData[0].value + pieData[1].value)) * 100 || 0)}%
              </span>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Efficiency</span>
            </div>
          </div>

          <div className="w-full mt-6 space-y-3">
            {pieData.map((d, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i] }} />
                  <span className="text-xs font-bold text-slate-500">{d.name}</span>
                </div>
                <span className="text-xs font-black text-slate-900">{d.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RANKINGS CARD */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-50 rounded-xl border border-emerald-100 text-emerald-600">
                <LayoutGrid size={20} />
              </div>
              <h3 className="text-lg font-black text-slate-900 tracking-tight">Рейтинг подразделений</h3>
            </div>

            {/* Category Switcher — HR sees only employee departments */}
            <div className="flex p-1 bg-slate-100 rounded-xl border border-slate-200">
              {(
                <button
                  onClick={() => setTab('groups')}
                  className={clsx(
                    "px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                    tab === 'groups' ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"
                  )}
                >
                  Группы
                </button>
              )}
              <button
                onClick={() => setTab('departments')}
                className={clsx(
                  "px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all",
                  tab === 'departments' ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"
                )}
              >
                Отделы
              </button>
            </div>
          </div>

          {/* List Section */}
          <div className="space-y-4 max-h-[420px] overflow-y-auto pr-2 custom-scrollbar">
            {tab === 'groups' && (groupStats || []).map((g) => (
              <div key={g.group_id} className="group p-4 bg-slate-50 rounded-2xl border border-slate-100 transition-all hover:bg-white hover:border-indigo-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-xl flex items-center justify-center text-xs font-black text-slate-400 group-hover:text-indigo-600 transition-colors">
                    {g.group_name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-black text-slate-900 truncate">{g.group_name}</h4>
                      <span className={clsx(
                        "text-xs font-black tracking-tight",
                        g.on_time_percent >= 80 ? "text-emerald-500" : g.on_time_percent >= 60 ? "text-amber-500" : "text-rose-500"
                      )}>
                        {g.on_time_percent}%
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={clsx(
                            "h-full rounded-full transition-all duration-1000",
                            g.on_time_percent >= 80 ? "bg-emerald-500" : g.on_time_percent >= 60 ? "bg-amber-500" : "bg-rose-500"
                          )}
                          style={{ width: `${g.on_time_percent}%` }}
                        />
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Users size={12} className="text-slate-400" />
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{g.students_count}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {tab === 'departments' && (deptStats || []).map((d) => {
              const isSelected = selectedDeptId === d.department_id
              return (
                <div key={d.department_id} className="rounded-2xl border overflow-hidden transition-all">
                  <button
                    onClick={() => setSelectedDeptId(isSelected ? null : d.department_id)}
                    className={clsx(
                      "w-full group p-4 flex items-center gap-4 text-left transition-all",
                      isSelected ? "bg-indigo-50 border-indigo-200" : "bg-slate-50 border-slate-100 hover:bg-white hover:border-indigo-100"
                    )}
                  >
                    <div className="w-12 h-12 bg-white border border-slate-200 rounded-xl flex items-center justify-center text-indigo-500 shrink-0">
                      <Building2 size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-black text-slate-900 truncate">{d.department_name}</h4>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-black text-indigo-600">{d.on_time_percent}%</span>
                          {isSelected ? <ChevronUp size={14} className="text-indigo-500" /> : <ChevronDown size={14} className="text-slate-400" />}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 rounded-full transition-all duration-1000" style={{ width: `${d.on_time_percent}%` }} />
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Briefcase size={12} className="text-slate-400" />
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{d.employees_count}</span>
                        </div>
                      </div>
                    </div>
                  </button>

                  {isSelected && (
                    <div className="border-t border-indigo-100 bg-white p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          <Clock size={12} /> Отчёт за месяц
                        </div>
                        <button
                          onClick={() => handleExportDeptExcel(d.department_id, d.department_name)}
                          disabled={exportingDept}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-all disabled:opacity-50"
                        >
                          <Download size={12} /> Excel
                        </button>
                      </div>

                      {isDeptDetailLoading ? (
                        <div className="text-center py-6 text-slate-400 text-xs font-bold">Загрузка...</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-slate-100">
                                <th className="text-left py-2 pr-3 font-black text-slate-500 uppercase tracking-widest text-[9px]">Сотрудник</th>
                                <th className="text-center py-2 px-2 font-black text-slate-500 uppercase tracking-widest text-[9px]">Приход</th>
                                <th className="text-center py-2 px-2 font-black text-slate-500 uppercase tracking-widest text-[9px]">Статус</th>
                                <th className="text-center py-2 px-2 font-black text-slate-500 uppercase tracking-widest text-[9px]">Дней</th>
                                <th className="text-center py-2 px-2 font-black text-amber-600 uppercase tracking-widest text-[9px]">Опозд.</th>
                                <th className="text-center py-2 px-2 font-black text-indigo-600 uppercase tracking-widest text-[9px]">Отраб. ч.</th>
                                <th className="text-center py-2 pl-2 font-black text-emerald-600 uppercase tracking-widest text-[9px]">Перераб.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(deptDetail?.employees_today || []).map((emp) => (
                                <tr key={emp.employee_id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                                  <td className="py-2.5 pr-3">
                                    <span className="font-bold text-slate-800 truncate block max-w-[160px]">{emp.full_name}</span>
                                    <span className="text-[9px] text-slate-400">{emp.position_name}</span>
                                  </td>
                                  <td className="text-center py-2.5 px-2 font-mono font-bold text-slate-700">
                                    {emp.check_in || '—'}
                                  </td>
                                  <td className="text-center py-2.5 px-2">
                                    <span className={clsx(
                                      "px-2 py-0.5 rounded-lg text-[9px] font-black uppercase",
                                      emp.status === 'on_time' ? "bg-emerald-50 text-emerald-700" :
                                      emp.status === 'late' ? "bg-amber-50 text-amber-700" :
                                      emp.status === 'very_late' ? "bg-rose-50 text-rose-700" :
                                      "bg-slate-100 text-slate-500"
                                    )}>
                                      {emp.status === 'on_time' ? 'Вовремя' :
                                       emp.status === 'late' ? 'Опоздал' :
                                       emp.status === 'very_late' ? 'Сильно' :
                                       emp.status === 'absent' ? 'Отсутствует' : emp.status}
                                    </span>
                                  </td>
                                  <td className="text-center py-2.5 px-2 font-bold text-slate-700">{emp.period_days_present ?? '—'}</td>
                                  <td className="text-center py-2.5 px-2">
                                    {emp.period_late_count > 0
                                      ? <span className="font-black text-amber-600 flex items-center justify-center gap-1"><AlertTriangle size={10} />{emp.period_late_count}</span>
                                      : <span className="text-slate-300">—</span>}
                                  </td>
                                  <td className="text-center py-2.5 px-2 font-bold text-indigo-600">{emp.period_worked_hours ?? '—'}</td>
                                  <td className="text-center py-2.5 pl-2 font-bold text-emerald-600">
                                    {emp.period_overtime_hours > 0 ? `+${emp.period_overtime_hours}` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function Briefcase({ size, className }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      className={className}
    >
      <rect width="20" height="14" x="2" y="7" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  )
}